import { extend, override } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import LinkButton from 'flarum/common/components/LinkButton';
import LogInButtons from 'flarum/forum/components/LogInButtons';
import LogInButton from 'flarum/forum/components/LogInButton';
import UserCard from 'flarum/forum/components/UserCard';
import UserPage from 'flarum/forum/components/UserPage';
import Link from 'flarum/common/components/Link';

// noinspection JSUnusedGlobalSymbols
export { default as extend } from './extend';
app.initializers.add('maicol07/flarum-oidc-client', () => {
  function route(id, append) {
    if (append === void 0) {
      append = '';
    }
    const routes = {
      login: 'auth/oidc'
    };
    return `${app.forum.attribute('baseUrl')}/${routes[id]}${append}`;
  }

  /**
   * Returns a setting added by the extension
   */
  function setting(slug, cast) {
    let setting = app.forum.attribute(`maicol07-oidc-client.${slug}`);
    if (cast !== undefined) {
      if (cast === Boolean && !isNaN(setting)) {
        setting = Number(setting);
      }
      return cast(setting);
    }
    return setting;
  }

  // Source: https://github.com/FriendsOfFlarum/oauth/blob/a40e562eeb32bda8484362db5b0883a400e55208/js/src/forum/index.ts#L11-L63
  const params = new URLSearchParams(window.location.search);

  // Detect the _flarum_auth query parameter added by ResponseFactory after a
  // new-user OAuth callback. Strip it from the URL, fetch the token data from
  // the API to pre-populate the SignUpModal, then open the modal.
  const authToken = params.get('_flarum_auth');
  if (authToken) {
    params.delete('_flarum_auth');
    const clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
    window.history.replaceState({}, '', clean);

    // Defer until after app.mount() has run so the modal manager is mounted,
    // then resolve the token to username/email/provided before showing the modal.
    setTimeout(async () => {
      let modalAttrs = {
        token: authToken
      };
      try {
        const response = await app.request({
          method: 'POST',
          url: app.forum.attribute('apiUrl') + '/registration-token',
          body: {
            token: authToken
          }
        });
        modalAttrs = {
          token: authToken,
          username: response?.username ?? '',
          email: response?.email ?? '',
          provided: response?.provided ?? []
        };
      } catch {
        // If the fetch fails (e.g. token already used), fall back to opening
        // the modal with just the token — it will show empty fields.
      }
      await app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal'), modalAttrs);
    }, 0);
  }

  // Detect the _flarum_linked query parameter added after a successful account
  // link (both manual from the security page and auto email-match on login).
  // Strip it from the URL and show the AccountLinkedModal.
  const linkedProvider = params.get('_flarum_linked');
  if (linkedProvider) {
    params.delete('_flarum_linked');
    const clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
    window.history.replaceState({}, '', clean);
  }

  // Source: https://github.com/FriendsOfFlarum/oauth/blob/a40e562eeb32bda8484362db5b0883a400e55208/js/src/forum/extenders/extendLoginSignup.tsx#L29
  // Replace the popup onclick with a full-page navigation.
  https: override(LogInButton, 'initAttrs', function (original, attrs) {
    original(attrs);

    // Override whatever onclick the parent set — we navigate instead of opening a popup.
    attrs.onclick = function (e) {
      e.preventDefault();
      window.location.href = attrs.path;
    };
  });
  extend(HeaderSecondary.prototype, 'items', items => {
    if (setting('remove_signup_btn', Boolean)) {
      items.remove('signUp');
    }
    const loginUrl = route('login');
    if (setting('sso_mode', Boolean)) {
      if (!items.has('logIn')) {
        return;
      }
      items.setContent('logIn', m("a", {
        href: loginUrl,
        className: "Button Button--link"
      }, app.translator.trans('core.forum.header.log_in_link')));
      override('flarum/forum/components/LogInModal', 'oninit', () => {
        window.location.href = loginUrl;
        throw new Error('Stop execution');
      });
      if (!items.has('signUp')) {
        return;
      }
      items.setContent('signUp', m("a", {
        href: loginUrl,
        className: "Button Button--link"
      }, app.translator.trans('core.forum.header.sign_up_link')));
      override('flarum/forum/components/SignUpModal', 'oninit', () => {
        window.location.href = loginUrl;
        throw new Error('Stop execution');
      });
    } else {
      extend(LogInButtons.prototype, 'items', items => {
        items.add('oidcLogin', m(LogInButton, {
          class: "Button LogInButton--oidc",
          path: "/auth/oidc",
          icon: "fab fa-openid"
        }, app.translator.trans('maicol07-oidc-client.forum.buttons.login', {
          name: setting('provider_name')
        })));
      });
      if (setting('remove_signup_btn', Boolean)) {
        override('flarum/forum/components/LogInModal', 'footer', original => {
          const footer = original();
          if (footer.children && Array.isArray(footer.children)) {
            footer.children = footer.children.filter(element => typeof element === 'object' && !Array.isArray(element) && element?.attrs.className !== 'LogInModal-signUp');
          }
          return footer;
        });
      }
    }
  });

  // @ts-ignore
  extend('flarum/forum/components/SettingsPage', 'accountItems', function (items) {
    if (this.user?.isLinkedToOidcProvider() && items.has('changeEmail')) {
      items.remove('changeEmail');
    }

    // @ts-ignore
    if (this.user?.isLinkedToOidcProvider() && items.has('changePassword')) {
      items.remove('changePassword');
    }

    // @ts-ignore
    if (this.user?.isLinkedToOidcProvider() && setting('manage_account_url', Boolean)) {
      items.add('manageAccount', m(Link, {
        class: "Button",
        external: true,
        href: setting('manage_account_url'),
        target: setting('manage_account_btn_open_in_new_tab') ? '_blank' : ''
      }, m("i", {
        class: "fab fa-openid"
      }), app.translator.trans('maicol07-oidc-client.forum.buttons.manage_account')));
    }
    if (!setting('sso_mode', Boolean)) {
      items.add('connectOIDC', m(LinkButton, {
        icon: "fab fa-openid",
        class: "Button",
        external: true
        // @ts-ignore
        ,
        href: route('login', `?action=${app.session.user?.isLinkedToOidcProvider() ? 'disconnect' : 'connect'}`)
      }, app.translator.trans(
      // @ts-ignore
      `maicol07-oidc-client.forum.buttons.${app.session.user?.isLinkedToOidcProvider() ? 'disconnect_account' : 'connect_account'}`, {
        name: setting('provider_name')
      })));
    }
  });
  extend(UserPage.prototype, 'view', vnode => {
    if (setting('sync_avatars', Boolean) && Array.isArray(vnode.children) && vnode.children[0].tag === UserCard) {
      // @ts-expect-error - Vnode doesn't have attrs type
      vnode.children[0].attrs.editable = false;
    }
  });
});